import type { IrregularWord, Sentence } from '../../types/content'
import { wordById } from './words'

export const irregularWords: IrregularWord[] = [
  { id: 'irregular_a', spelling: 'a', regularPart: '', irregularPart: 'a', noteZh: '这个小词在句子里常读作 /ə/。', audioId: 'word_a_ga' },
  { id: 'irregular_the', spelling: 'the', regularPart: 'th', irregularPart: 'e', noteZh: 'th 可以读，最后一小部分需要记住。', audioId: 'word_the_ga' },
  { id: 'irregular_was', spelling: 'was', regularPart: 'w', irregularPart: 'as', noteZh: '开头可以读，后面需要记住。', audioId: 'word_was_ga' },
  { id: 'irregular_said', spelling: 'said', regularPart: 's,d', irregularPart: 'ai', noteZh: '中间这部分不像常见 ai，需要记住。', audioId: 'word_said_ga' },
  { id: 'irregular_i', spelling: 'I', regularPart: '', irregularPart: 'I', noteZh: '这是一个需要整体记住的小词。', audioId: 'word_i_ga' },
  { id: 'irregular_to', spelling: 'to', regularPart: 't', irregularPart: 'o', noteZh: '结尾的声音需要记住。', audioId: 'word_to_ga' },
  { id: 'irregular_of', spelling: 'of', regularPart: 'o', irregularPart: 'f', noteZh: '最后的 f 在这个词里读 /v/。', audioId: 'word_of_ga' },
  { id: 'irregular_you', spelling: 'you', regularPart: '', irregularPart: 'you', noteZh: '这个常用词需要整体记住。', audioId: 'word_you_ga' },
  { id: 'irregular_is', spelling: 'is', regularPart: 'i', irregularPart: 's', noteZh: '这个常用词的 s 读 /z/，先整体记住。', audioId: 'word_is_ga' },
]

interface SentenceSeed {
  text: string
  words: string[]
  irregular?: string[]
  stage: number
  difficulty: Sentence['difficulty']
}

const seeds: SentenceSeed[] = [
  { text: 'A cat sat.', words: ['cat', 'sat'], irregular: ['a'], stage: 2, difficulty: 1 },
  { text: 'A dog can dig.', words: ['dog', 'can', 'dig'], irregular: ['a'], stage: 2, difficulty: 1 },
  { text: 'The hen can run.', words: ['hen', 'can', 'run'], irregular: ['the'], stage: 3, difficulty: 1 },
  { text: 'I can hop.', words: ['can', 'hop'], irregular: ['i'], stage: 3, difficulty: 1 },
  { text: 'The fox is in the box.', words: ['fox', 'in', 'box'], irregular: ['the', 'is'], stage: 4, difficulty: 2 },
  { text: 'The duck is on the rock.', words: ['duck', 'on', 'rock'], irregular: ['the', 'is'], stage: 5, difficulty: 2 },
  { text: 'The fish is in the dish.', words: ['fish', 'in', 'dish'], irregular: ['the', 'is'], stage: 6, difficulty: 2 },
  { text: 'The king can sing.', words: ['king', 'can', 'sing'], irregular: ['the'], stage: 6, difficulty: 2 },
  { text: 'The frog can swim.', words: ['frog', 'can', 'swim'], irregular: ['the'], stage: 7, difficulty: 2 },
  { text: 'The lamp is on the desk.', words: ['lamp', 'on', 'desk'], irregular: ['the', 'is'], stage: 7, difficulty: 2 },
  { text: 'I ride a bike.', words: ['ride', 'bike'], irregular: ['i', 'a'], stage: 8, difficulty: 2 },
  { text: 'The kite is safe.', words: ['kite', 'safe'], irregular: ['the', 'is'], stage: 8, difficulty: 2 },
  { text: 'The green seed can grow.', words: ['green', 'seed', 'can', 'grow'], irregular: ['the'], stage: 9, difficulty: 3 },
  { text: 'The goat is on the road.', words: ['goat', 'on', 'road'], irregular: ['the', 'is'], stage: 9, difficulty: 3 },
  { text: 'Look at the moon.', words: ['look', 'at', 'moon'], irregular: ['the'], stage: 10, difficulty: 3 },
  { text: 'The book is in the room.', words: ['book', 'in', 'room'], irregular: ['the', 'is'], stage: 10, difficulty: 3 },
  { text: 'The bird is on the farm.', words: ['bird', 'on', 'farm'], irregular: ['the', 'is'], stage: 11, difficulty: 3 },
  { text: 'The storm is at the park.', words: ['storm', 'at', 'park'], irregular: ['the', 'is'], stage: 11, difficulty: 3 },
  { text: 'The boy can point.', words: ['boy', 'can', 'point'], irregular: ['the'], stage: 12, difficulty: 3 },
  { text: 'The cow is out.', words: ['cow', 'out'], irregular: ['the', 'is'], stage: 12, difficulty: 3 },
  { text: 'The deer is near.', words: ['deer', 'near'], irregular: ['the', 'is'], stage: 13, difficulty: 4 },
  { text: 'The phone is on the chair.', words: ['phone', 'on', 'chair'], irregular: ['the', 'is'], stage: 14, difficulty: 4 },
  { text: 'The dogs played.', words: ['dogs', 'played'], irregular: ['the'], stage: 17, difficulty: 4 },
  { text: 'The cats jumped.', words: ['cats', 'jumped'], irregular: ['the'], stage: 17, difficulty: 4 },
  { text: 'The sunset is red.', words: ['sunset', 'red'], irregular: ['the', 'is'], stage: 18, difficulty: 4 },
  { text: 'The raincoat is inside.', words: ['raincoat', 'inside'], irregular: ['the', 'is'], stage: 18, difficulty: 5 },
]

export const sentences: Sentence[] = seeds.map((seed, index) => {
  const wordIds = seed.words.map((spelling) => `word_${spelling}`)
  const missing = wordIds.filter((id) => !wordById.has(id))
  if (missing.length) throw new Error(`Sentence word is missing: ${missing.join(', ')}`)
  const requiredConceptIds = Array.from(new Set(wordIds.flatMap((id) => wordById.get(id)?.decodableAfterConcepts ?? [])))
  return {
    id: `sentence_${String(index + 1).padStart(2, '0')}`,
    text: seed.text,
    wordIds,
    requiredConceptIds,
    irregularWordIds: (seed.irregular ?? []).map((word) => `irregular_${word}`),
    audioId: `sentence_${String(index + 1).padStart(2, '0')}_ga`,
    difficulty: seed.difficulty,
    introducedAtStage: seed.stage,
  }
})
